import { ArrowRight, ExternalLink, MailCheck, RotateCw } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../lib/cn'
import { Button } from '../button'

export interface CheckYourEmailProps {
  /** The address the link was sent to (rendered emphasized in the body). */
  email: string
  /** Seconds before "Resend" unlocks. Counts down internally. Default 45. */
  resendSeconds?: number
  /** Trailing clause after the address. Default mentions a 15-minute expiry. */
  expiresLabel?: string
  onResend?: () => void
  onOpenMail?: () => void
  onUseDifferentMethod?: () => void
  className?: string
}

function formatMs(total: number): string {
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * CheckYourEmail — the "we sent you a link" confirmation state for magic-link
 * (and any emailed-link) flows. Owns the resend countdown internally; the
 * caller wires resend / open-mail / change-method to the underlying flow.
 */
export const CheckYourEmail = React.forwardRef<HTMLDivElement, CheckYourEmailProps>(
  (
    {
      email,
      resendSeconds = 45,
      expiresLabel = 'the link expires in 15 minutes',
      onResend,
      onOpenMail,
      onUseDifferentMethod,
      className,
    },
    ref,
  ) => {
    const [remaining, setRemaining] = React.useState(resendSeconds)

    React.useEffect(() => {
      if (remaining <= 0) return
      const t = setInterval(() => setRemaining((r) => (r <= 1 ? 0 : r - 1)), 1000)
      return () => clearInterval(t)
    }, [remaining])

    const canResend = remaining <= 0

    return (
      <div ref={ref} className={cn('flex flex-col items-center gap-5 text-center', className)}>
        <span className="flex size-14 items-center justify-center rounded-2xl bg-ia-indigo-muted text-ia-indigo">
          <MailCheck className="size-7" />
        </span>

        <div className="flex flex-col gap-1.5">
          <h2 className="text-heading-xl text-ia-text">Check your email</h2>
          <p className="text-body-md text-ia-text-secondary">
            We sent a link to <span className="font-medium text-ia-text">{email}</span>. Open it on
            this device to continue — {expiresLabel}.
          </p>
        </div>

        <button
          type="button"
          disabled={!canResend}
          onClick={() => {
            if (!canResend) return
            onResend?.()
            setRemaining(resendSeconds)
          }}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-pill border border-ia-border bg-ia-input-bg px-3.5 py-1.5 text-body-sm transition-colors',
            canResend
              ? 'text-ia-text hover:bg-ia-surface-hover hover:border-ia-border-strong'
              : 'text-ia-text-tertiary cursor-not-allowed',
          )}
        >
          <RotateCw className={cn('size-3.5', !canResend && 'opacity-60')} />
          {canResend ? 'Resend link' : `Resend in ${formatMs(remaining)}`}
        </button>

        <Button
          type="button"
          onClick={onOpenMail}
          className="h-11 w-full rounded-lg text-base font-semibold [&_svg]:size-4"
        >
          <ExternalLink />
          Open mail app
        </Button>

        {onUseDifferentMethod && (
          <button
            type="button"
            onClick={onUseDifferentMethod}
            className="inline-flex items-center gap-1 text-body-sm font-medium text-ia-text-secondary transition-colors hover:text-ia-text"
          >
            Use a different method
            <ArrowRight className="size-3.5" />
          </button>
        )}
      </div>
    )
  },
)
CheckYourEmail.displayName = 'CheckYourEmail'
